import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import * as React from 'react';

import HubRoundedIcon from '@mui/icons-material/HubRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import {
  FONT_BODY,
  FONT_DISPLAY,
  FONT_MONO,
  Reveal,
  SectionHeading,
  TEAL,
  glassCardSx,
  gridLayerSx,
} from './LandingKit';

const items = [
  {
    icon: <HubRoundedIcon />,
    title: 'Torre de Controle de coletas',
    description:
      'Veja cada motorista, o volume já coletado e o status de cada visita do dia numa tela só, atualizando ao vivo.',
    imageLight: `url("/tower.png")`,
    imageDark: `url("/tower.png")`,
  },
  {
    icon: <PhoneIphoneRoundedIcon />,
    title: 'Sem rastreador, sem instalação',
    description:
      'Funciona com o app no celular que o motorista já tem. Zero hardware para comprar, zero técnico em campo.',
    imageLight: `url("/analytics.png")`,
    imageDark: `url("/analytics.png")`,
  },
  {
    icon: <WhatsAppIcon />,
    title: 'Pergunte pela operação no WhatsApp',
    description:
      '"Quantas coletas faltam?" "Cadê o João?" A resposta chega no WhatsApp, com dado real, na hora.',
    imageLight: `url("/tower.png")`,
    imageDark: `url("/tower.png")`,
  },
];

// Selectable feature button — teal when active, glass-ish hover otherwise.
function FeatureButton({ item, index, selected, onClick }) {
  const { icon, title, description } = item;
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onClick(index)}
      sx={(theme) => ({
        appearance: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        p: 2.25,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: selected ? `${TEAL}66` : 'rgba(2,20,18,0.10)',
        backgroundColor: selected ? `${TEAL}14` : 'rgba(255,255,255,0.45)',
        backdropFilter: 'blur(8px)',
        boxShadow: selected ? `0 14px 36px ${TEAL}22` : 'none',
        transition:
          'border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: `${TEAL}55`,
          backgroundColor: selected ? `${TEAL}1f` : 'rgba(255,255,255,0.62)',
        },
        ...theme.applyStyles('dark', {
          borderColor: selected ? `${TEAL}66` : 'rgba(255,255,255,0.10)',
          backgroundColor: selected ? `${TEAL}1f` : 'rgba(12,18,20,0.4)',
          '&:hover': {
            transform: 'translateY(-2px)',
            borderColor: `${TEAL}66`,
            backgroundColor: selected ? `${TEAL}26` : 'rgba(12,18,20,0.55)',
          },
        }),
      })}
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1,
        })}
      >
        <Box
          sx={(theme) => ({
            display: 'inline-flex',
            color: selected ? TEAL : '#33514c',
            transition: 'color 0.18s ease',
            ...theme.applyStyles('dark', { color: selected ? TEAL : '#9fc4bd' }),
            '& svg': { fontSize: 26 },
          })}
        >
          {icon}
        </Box>
        <Typography
          sx={(theme) => ({
            fontFamily: FONT_DISPLAY,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '-0.005em',
            fontSize: '1rem',
            lineHeight: 1.1,
            color: selected ? '#071513' : '#1d3531',
            ...theme.applyStyles('dark', { color: selected ? '#f2fbf9' : '#cfeae5' }),
          })}
        >
          {title}
        </Typography>
        <Typography
          sx={(theme) => ({
            fontFamily: FONT_BODY,
            fontSize: '0.9rem',
            lineHeight: 1.45,
            color: '#33514c',
            ...theme.applyStyles('dark', { color: '#9fc4bd' }),
          })}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

FeatureButton.propTypes = {
  index: PropTypes.number.isRequired,
  item: PropTypes.shape({
    description: PropTypes.string.isRequired,
    icon: PropTypes.element,
    title: PropTypes.string.isRequired,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  selected: PropTypes.bool.isRequired,
};

// Glass image panel shared by mobile + desktop layouts.
function ImagePanel({ feature }) {
  const src = feature.imageLight.replace(/^url\(["']?(.*?)["']?\)$/, '$1');
  return (
    <Card
      elevation={0}
      sx={(theme) => ({ ...glassCardSx(theme), width: '100%', p: 1.5, overflow: 'hidden', '&:hover': undefined })}
    >
      <Box
        component="img"
        src={src}
        alt={feature.title}
        loading="lazy"
        sx={{ width: '100%', height: 'auto', display: 'block', borderRadius: 1.5 }}
      />
    </Card>
  );
}

ImagePanel.propTypes = {
  feature: PropTypes.shape({
    imageLight: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
};

function MobileLayout({ selectedItemIndex, handleItemClick, selectedFeature }) {
  if (!items[selectedItemIndex]) {
    return null;
  }

  return (
    <Box
      sx={{
        display: { xs: 'flex', sm: 'none' },
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, overflow: 'auto', pb: 0.5 }}>
        {items.map(({ title }, index) => {
          const active = selectedItemIndex === index;
          return (
            <Box
              key={index}
              component="button"
              type="button"
              onClick={() => handleItemClick(index)}
              sx={(theme) => ({
                appearance: 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
                fontFamily: FONT_MONO,
                fontSize: 11,
                letterSpacing: '0.06em',
                border: '1px solid',
                borderColor: active ? `${TEAL}66` : 'rgba(2,20,18,0.12)',
                backgroundColor: active ? `${TEAL}1f` : 'transparent',
                color: active ? '#0a3f39' : '#33514c',
                ...theme.applyStyles('dark', {
                  borderColor: active ? `${TEAL}66` : 'rgba(255,255,255,0.12)',
                  color: active ? '#7df0e2' : '#9fc4bd',
                }),
              })}
            >
              {title}
            </Box>
          );
        })}
      </Box>
      <ImagePanel feature={items[selectedItemIndex]} height={280} width={360} />
      <Box sx={{ px: 0.5 }}>
        <Typography
          sx={(theme) => ({
            fontFamily: FONT_DISPLAY,
            fontWeight: 700,
            textTransform: 'uppercase',
            fontSize: '1rem',
            color: '#071513',
            mb: 0.75,
            ...theme.applyStyles('dark', { color: '#f2fbf9' }),
          })}
        >
          {selectedFeature.title}
        </Typography>
        <Typography
          sx={(theme) => ({
            fontFamily: FONT_BODY,
            fontSize: '0.9rem',
            lineHeight: 1.45,
            color: '#33514c',
            ...theme.applyStyles('dark', { color: '#9fc4bd' }),
          })}
        >
          {selectedFeature.description}
        </Typography>
      </Box>
    </Box>
  );
}

MobileLayout.propTypes = {
  handleItemClick: PropTypes.func.isRequired,
  selectedFeature: PropTypes.shape({
    description: PropTypes.string.isRequired,
    icon: PropTypes.element,
    imageDark: PropTypes.string.isRequired,
    imageLight: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  selectedItemIndex: PropTypes.number.isRequired,
};

export { MobileLayout };

export default function Features() {
  const [selectedItemIndex, setSelectedItemIndex] = React.useState(0);

  const handleItemClick = (index) => {
    setSelectedItemIndex(index);
  };

  const selectedFeature = items[selectedItemIndex];

  return (
    <Box
      id="features"
      sx={(theme) => ({
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#f3f7f6',
        ...theme.applyStyles('dark', { backgroundColor: '#070b0e' }),
      })}
    >
      <Box aria-hidden sx={gridLayerSx} />
      <Container sx={{ position: 'relative', py: { xs: 10, sm: 18 } }}>
        <Reveal>
          <SectionHeading
            overline="FUNCIONALIDADES"
            title="Funcionalidades"
            subtitle="Tudo o que você precisa para enxergar e tocar suas coletas em tempo real, sem comprar nem instalar hardware."
          />
        </Reveal>

        <Reveal delay={0.1}>
          <Box
            sx={{
              mt: { xs: 5, sm: 7 },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row-reverse' },
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            <Box sx={{ width: { xs: '100%', md: '40%' } }}>
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  flexDirection: 'column',
                  gap: 1.5,
                  height: '100%',
                }}
              >
                {items.map((item, index) => (
                  <FeatureButton
                    key={index}
                    item={item}
                    index={index}
                    selected={selectedItemIndex === index}
                    onClick={handleItemClick}
                  />
                ))}
              </Box>
              <MobileLayout
                selectedItemIndex={selectedItemIndex}
                handleItemClick={handleItemClick}
                selectedFeature={selectedFeature}
              />
            </Box>

            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                width: { xs: '100%', md: '60%' },
              }}
            >
              <ImagePanel feature={items[selectedItemIndex]} />
            </Box>
          </Box>
        </Reveal>
      </Container>
    </Box>
  );
}
